import { useState } from 'react';
import api from '@/api';
import { X, Upload, Plus, Trash2, Loader2, FileText, Sparkles, CheckCircle2 } from 'lucide-react';

const DEFAULT_TRADES = [
  { csi_number: '260000', description: 'Electrical' },
  { csi_number: '230000', description: 'HVAC' },
  { csi_number: '220000', description: 'Plumbing' },
  { csi_number: '210000', description: 'Fire Protection' },
  { csi_number: '092000', description: 'Drywall' },
  { csi_number: '075000', description: 'Roofing' },
  { csi_number: '081000', description: 'Doors, Frames & Hardware' },
  { csi_number: '099000', description: 'Painting' },
  { csi_number: '095100', description: 'Acoustic Ceilings' },
  { csi_number: '096000', description: 'Carpet / Resilient Flooring' },
  { csi_number: '280000', description: 'Security' },
  { csi_number: '270000', description: 'IT / Data' },
];

export default function G703ImportModal({ projectId, onClose, onImported }) {
  const [mode, setMode] = useState('manual'); // 'manual' | 'csv' | 'ai'
  const [lines, setLines] = useState([{ csi_number: '', description: '', vendor_name: '', scheduled_value: '' }]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [aiParsing, setAiParsing] = useState(false);
  const [aiFile, setAiFile] = useState(null);

  const addLine = () => setLines([...lines, { csi_number: '', description: '', vendor_name: '', scheduled_value: '' }]);

  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const updateLine = (i, field, val) => {
    const updated = [...lines];
    updated[i] = { ...updated[i], [field]: val };
    setLines(updated);
  };

  const loadDefaults = () => {
    setLines(DEFAULT_TRADES.map(t => ({ ...t, vendor_name: '', scheduled_value: '' })));
  };

  const handleCSVPaste = (text) => {
    const rows = text.trim().split('\n').filter(r => r.trim());
    const parsed = rows.map(r => {
      const cols = r.split(/[,\t]/).map(c => c.trim());
      return {
        csi_number: cols[0] || '',
        description: cols[1] || '',
        vendor_name: cols[2] || '',
        scheduled_value: cols[3] || '',
      };
    }).filter(r => r.csi_number && r.description);
    if (parsed.length) setLines(parsed);
  };

  const handleAIUpload = async () => {
    if (!aiFile) return;
    setAiParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', aiFile);
      const res = await api.post(
        `/closeout/projects/${projectId}/parse-g703-document`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.parsed_lines?.length) {
        setLines(res.data.parsed_lines.map(l => ({
          csi_number: l.csi_number,
          description: l.description,
          vendor_name: l.vendor_name || '',
          scheduled_value: l.scheduled_value || '',
        })));
        setMode('manual'); // Switch to manual so they can review/edit before importing
      }
    } catch (err) {
      console.error('AI parse failed:', err);
    } finally {
      setAiParsing(false);
    }
  };

  const handleImport = async () => {
    const validLines = lines.filter(l => l.csi_number && l.description);
    if (!validLines.length) return;

    setImporting(true);
    try {
      const payload = {
        lines: validLines.map((l, i) => ({
          line_number: String(i + 1),
          csi_number: l.csi_number,
          description: l.description,
          vendor_name: l.vendor_name || null,
          scheduled_value: l.scheduled_value ? parseFloat(l.scheduled_value) : null,
        })),
      };
      const res = await api.post(`/closeout/projects/${projectId}/import-g703`, payload);
      setResult(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setImporting(false);
    }
  };

  const inputCls = "w-full bg-card border border-border rounded-sm px-2 py-1.5 text-xs text-foreground font-mono focus:outline-none focus:border-primary/40/50 placeholder:text-muted-foreground";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="g703-import-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-sm w-[800px] max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground">Import G703 — Schedule of Values</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Each SOV line becomes a trade division with 9 standard folders (A-I)</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground/80">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {result ? (
            /* Success state */
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
              <p className="text-lg font-heading text-foreground mb-2">{result.divisions_created} Divisions Created</p>
              <p className="text-sm text-muted-foreground mb-6">Each with 9 standard closeout folders (A-I)</p>
              <button onClick={onImported} data-testid="import-done-btn"
                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-heading uppercase tracking-wider rounded-sm transition-colors">
                View Matrix
              </button>
            </div>
          ) : (
            <>
              {/* Mode tabs */}
              <div className="flex gap-2 mb-4">
                <button onClick={() => setMode('ai')}
                  data-testid="g703-mode-ai"
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors ${mode === 'ai' ? 'bg-violet-600/20 text-violet-400 border border-violet-900' : 'text-muted-foreground hover:text-foreground/80'}`}>
                  <Sparkles className="w-3 h-3" /> AI Reader
                </button>
                <button onClick={() => setMode('manual')}
                  data-testid="g703-mode-manual"
                  className={`px-3 py-1.5 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors ${mode === 'manual' ? 'bg-primary/15 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80'}`}>
                  Manual Entry
                </button>
                <button onClick={() => setMode('csv')}
                  data-testid="g703-mode-csv"
                  className={`px-3 py-1.5 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors ${mode === 'csv' ? 'bg-primary/15 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80'}`}>
                  Paste CSV
                </button>
                <div className="flex-1" />
                {mode === 'manual' && (
                  <button onClick={loadDefaults} className="text-xs text-muted-foreground hover:text-primary transition-colors">
                    Load Default Trades
                  </button>
                )}
              </div>

              {mode === 'ai' ? (
                /* AI Upload Mode */
                <div className="space-y-4" data-testid="g703-ai-upload">
                  <div className="border-2 border-dashed border-violet-800/40 rounded-sm p-8 text-center hover:border-violet-600/50 transition-colors">
                    <Sparkles className="w-10 h-10 text-violet-400 mx-auto mb-3" />
                    <p className="text-sm text-foreground/80 mb-2">Upload a G703 SOV document</p>
                    <p className="text-xs text-muted-foreground mb-4">PDF, PNG, JPG — AI will extract all trade lines automatically</p>
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600/15 border border-violet-700/50 rounded-sm text-xs font-heading uppercase tracking-wider text-violet-400 cursor-pointer hover:bg-violet-600/25 transition-colors">
                      <Upload className="w-3.5 h-3.5" />
                      {aiFile ? aiFile.name : 'Choose File'}
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif"
                        className="hidden"
                        onChange={(e) => setAiFile(e.target.files[0])}
                        data-testid="g703-ai-file-input"
                      />
                    </label>
                  </div>

                  {aiFile && (
                    <div className="flex items-center justify-between p-3 bg-muted/50 border border-border rounded-sm">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-violet-400" />
                        <span className="text-xs font-mono text-foreground/80">{aiFile.name}</span>
                      </div>
                      <button
                        onClick={handleAIUpload}
                        disabled={aiParsing}
                        data-testid="g703-ai-parse-btn"
                        className="flex items-center gap-2 px-4 py-1.5 bg-violet-600/20 border border-violet-700/50 rounded-sm text-xs font-heading uppercase tracking-wider text-violet-400 hover:bg-violet-600/30 disabled:opacity-50 transition-colors"
                      >
                        {aiParsing ? (
                          <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading...</>
                        ) : (
                          <><Sparkles className="w-3.5 h-3.5" /> Extract with AI</>
                        )}
                      </button>
                    </div>
                  )}

                  <div className="flex items-start gap-2 p-3 bg-amber-950/20 border border-amber-800/20 rounded-sm">
                    <CheckCircle2 className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <p className="text-[10px] text-amber-400/80 leading-relaxed">
                      AI will extract CSI numbers, trade descriptions, vendors, and contract values.
                      Results are shown for your review before importing — you can edit any field.
                    </p>
                  </div>
                </div>
              ) : mode === 'csv' ? (
                <textarea
                  className="w-full h-48 bg-card border border-border rounded-sm p-3 text-xs text-foreground font-mono focus:outline-none focus:border-primary/40/50 placeholder:text-muted-foreground resize-none"
                  placeholder="Paste CSV or tab-separated data:\n260000, Electrical, JW Electric, 1850000\n230000, HVAC, Metro Mechanical, 2100000"
                  onChange={(e) => handleCSVPaste(e.target.value)}
                  data-testid="csv-paste-area"
                />
              ) : (
                <>
                  {/* Table header */}
                  <div className="grid grid-cols-[100px_1fr_1fr_120px_32px] gap-2 mb-2">
                    <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground px-1">CSI #</span>
                    <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground px-1">Description</span>
                    <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground px-1">Vendor</span>
                    <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground px-1">SOV Value</span>
                    <span />
                  </div>

                  {/* Lines */}
                  <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
                    {lines.map((line, i) => (
                      <div key={i} className="grid grid-cols-[100px_1fr_1fr_120px_32px] gap-2" data-testid={`g703-line-${i}`}>
                        <input className={inputCls} value={line.csi_number} onChange={e => updateLine(i, 'csi_number', e.target.value)}
                          placeholder="260000" data-testid={`g703-csi-${i}`} />
                        <input className={inputCls} value={line.description} onChange={e => updateLine(i, 'description', e.target.value)}
                          placeholder="Electrical" data-testid={`g703-desc-${i}`} />
                        <input className={inputCls} value={line.vendor_name} onChange={e => updateLine(i, 'vendor_name', e.target.value)}
                          placeholder="JW Electric" />
                        <input className={inputCls} value={line.scheduled_value} onChange={e => updateLine(i, 'scheduled_value', e.target.value)}
                          placeholder="1,850,000" type="number" />
                        <button onClick={() => removeLine(i)} className="text-muted-foreground hover:text-red-400 transition-colors flex items-center justify-center">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button onClick={addLine} className="flex items-center gap-1 mt-3 text-xs text-muted-foreground hover:text-primary transition-colors">
                    <Plus className="w-3 h-3" /> Add Line
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!result && mode !== 'ai' && (
          <div className="flex justify-end gap-3 p-5 border-t border-border">
            <button onClick={onClose} className="px-4 py-2 text-xs font-heading uppercase tracking-wider text-muted-foreground hover:text-foreground">
              Cancel
            </button>
            <button onClick={handleImport} disabled={importing} data-testid="import-g703-submit"
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-heading uppercase tracking-wider rounded-sm transition-colors">
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Import {lines.filter(l => l.csi_number && l.description).length} Divisions
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
